/* eslint-disable */
import React, { useRef, useEffect, useState, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
    Box,
    Typography,
    Button,
    IconButton,
    TextField,
    CircularProgress,
    Stack,
    Divider,
} from '@mui/material'
import Person4Icon from '@mui/icons-material/Person4'
import SendIcon from '@mui/icons-material/Send'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import EmailIcon from '@mui/icons-material/Email'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import PushPinIcon from '@mui/icons-material/PushPin'
import CloseIcon from '@mui/icons-material/Close'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material'
import axios from 'axios'
import ReactMarkdown from 'react-markdown'
import { isAdmin } from '../../utils/auth'

function CourseDropdown({
    courses,
    selectedCourse,
    handleDropdownClick,
    handleCourseChange,
    dropdownPlaceholder,
}) {
    return (
        <Box
            sx={{
                p: '10px',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
            }}
        >
            <Typography
                component="span"
                sx={{
                    mr: '10px',
                    fontWeight: 'bold',
                    color: 'var(--color-primary)',
                }}
            >
                Course:
            </Typography>
            <select
                onClick={handleDropdownClick}
                onChange={handleCourseChange}
                value={selectedCourse || 'Please choose a course'}
                style={{
                    height: '40px',
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--color-border)',
                    borderRadius: '4px',
                    borderColor: 'var(--color-border)',
                    backgroundColor: 'var(--color-surface-1)',
                    color: 'var(--color-text)',
                    fontSize: '16px',
                    fontFamily: 'Italic',
                    flex: 1,
                    minWidth: 0,
                    textOverflow: 'ellipsis',
                }}
            >
                <option value="" disabled hidden>
                    {dropdownPlaceholder}
                </option>
                <option value="Please choose a course">
                    Please choose a course
                </option>
                {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                        {course.name}
                    </option>
                ))}
            </select>
        </Box>
    )
}

function ContactForm({
    staffEmails,
    professorLastName,
    studentName,
    courseName,
    onSubmit,
    onCancel,
}) {
    const initialSubject = `${studentName} from ${courseName} wants to contact you`
    const [message, setMessage] = useState(
        `Dear Professor ${professorLastName},\n\nHope you are doing well.\n\nBest Regards,\n${studentName}`
    )
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSent, setIsSent] = useState(false)

    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsSubmitting(true)
        try {
            await onSubmit(message, initialSubject)
            setIsSent(true)
        } catch (error) {
            console.error('Error sending email:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    if (isSent) {
        return (
            <Typography
                variant="body2"
                sx={{
                    color: 'var(--color-nav-text)',
                    backgroundColor: '#4caf50',
                    p: 1,
                    borderRadius: '10px',
                    mt: 1,
                    fontStyle: 'italic',
                }}
            >
                Message sent successfully to the course staff!
            </Typography>
        )
    }

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                mt: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
                maxWidth: '500px',
            }}
        >
            <Typography
                variant="subtitle1"
                sx={{
                    mb: 1,
                    fontWeight: 'bold',
                    color: 'var(--color-primary)',
                }}
            >
                Please fill out the form below to contact the course staff:
            </Typography>
            <TextField
                fullWidth
                label="To"
                value={staffEmails.join(', ')}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                sx={{
                    backgroundColor: 'var(--color-surface-2)',
                    borderRadius: '4px',
                    '& .MuiInputBase-input': {
                        color: 'var(--color-text)',
                        WebkitTextFillColor: 'var(--color-text)',
                    },
                    '& .MuiInputLabel-root': {
                        color: 'var(--color-text-muted)',
                    },
                    '& .MuiInputLabel-root.Mui-disabled': {
                        color: 'var(--color-text-muted)',
                    },
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'var(--color-border)' },
                    },
                }}
            />
            <TextField
                fullWidth
                label="Subject"
                value={initialSubject}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                sx={{
                    backgroundColor: 'var(--color-surface-2)',
                    borderRadius: '4px',
                    '& .MuiInputBase-input': {
                        color: 'var(--color-text)',
                        WebkitTextFillColor: 'var(--color-text)',
                    },
                    '& .MuiInputLabel-root': {
                        color: 'var(--color-text-muted)',
                    },
                    '& .MuiInputLabel-root.Mui-disabled': {
                        color: 'var(--color-text-muted)',
                    },
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'var(--color-border)' },
                    },
                }}
            />
            <TextField
                multiline
                rows={6}
                fullWidth
                variant="outlined"
                label="Your Message"
                placeholder="Type your message to the course staff..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                size="small"
                disabled={isSubmitting}
                sx={{
                    backgroundColor: 'var(--color-surface-1)',
                    borderRadius: '4px',
                    '& .MuiInputBase-input': {
                        color: 'var(--color-text)',
                    },
                    '& .MuiInputLabel-root': {
                        color: 'var(--color-text-muted)',
                    },
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'var(--color-border)' },
                        '&:hover fieldset': {
                            borderColor: 'var(--color-primary)',
                        },
                    },
                }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    disabled={isSubmitting || !message.trim()}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: 'var(--color-primary)',
                        color: '#ffffff !important',
                        '&:hover': {
                            backgroundColor: 'var(--color-primary-hover)',
                        },
                    }}
                >
                    {isSubmitting ? 'Sending...' : 'Send Email'}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onCancel}
                    disabled={isSubmitting}
                    sx={{
                        textTransform: 'none',
                        color: 'var(--color-text)',
                        borderColor: 'var(--color-border)',
                        backgroundColor: 'var(--color-surface-2)',
                        '&:hover': {
                            backgroundColor: 'var(--color-surface-3)',
                            borderColor: 'var(--color-primary)',
                        },
                    }}
                >
                    Cancel
                </Button>
            </Box>
        </Box>
    )
}

function VirtualTA({
    initialQuestion,
    courseId,
    initialCourseId,
    handleClose,
} = {}) {
    const activeCourseId = courseId || initialCourseId
    const [question, setQ] = useState(initialQuestion || '')
    const [error, setError] = useState('')
    const [conversationHistory, setConversationHistory] = useState([])
    const [selectedCourse, setSelectedCourse] = useState(activeCourseId || null)
    const [showCourseDropdown, setShowCourseDropdown] = useState(
        activeCourseId ? true : false
    )
    const conversationRef = useRef(null)
    const userData = useSelector((state) => state.userData)
    const userIsAdmin = isAdmin(userData?.user)
    const [queryInProgress, setQueryInProgress] = useState(false)
    const [isFetchingHistory, setIsFetchingHistory] = useState(false)
    const [initialMessagesShown, setInitialMessagesShown] = useState(false)
    const [limitReached, setLimitReached] = useState(false)
    const [isUnlimited, setIsUnlimited] = useState(false)
    const [tokensRemaining, setTokensRemaining] = useState(null)
    const [tokenLimit, setTokenLimit] = useState(null)
    const [resetsAt, setResetsAt] = useState(null)
    const [countdown, setCountdown] = useState('')
    const unlimitedAccess = isUnlimited || userIsAdmin
    const [dropdownPlaceholder, setDropdownPlaceholder] = useState(
        'Please choose a course'
    )
    const [virtualTAOpenTime, setVirtualTAOpenTime] = useState(false)
    const [showWelcomeMessage, setShowWelcomeMessage] = useState(false)
    const [selectedCourseName, setSelectedCourseName] = useState(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [courses, setCourses] = useState(null)
    const [fileToken, setFileToken] = useState('')
    const [courseStaff, setCourseStaff] = useState({
        emails: [],
        professorLastName: '',
    })
    const history = useHistory()

    // Sync selectedCourse if activeCourseId prop changes
    useEffect(() => {
        if (activeCourseId) {
            setSelectedCourse(activeCourseId)
            setInitialMessagesShown(false)
            setShowCourseDropdown(true)
            fetchRecentChats(activeCourseId)
        }
    }, [activeCourseId])

    // Sync input question if initialQuestion prop changes
    useEffect(() => {
        if (initialQuestion) {
            setQ(initialQuestion)
        }
    }, [initialQuestion])

    // Load recent chats whenever selectedCourse changes
    useEffect(() => {
        if (selectedCourse && selectedCourse !== 'Please choose a course') {
            fetchRecentChats(selectedCourse)
        }
    }, [selectedCourse])

    useEffect(() => {
        if (!resetsAt) return
        const interval = setInterval(() => {
            const now = new Date()
            const reset = new Date(resetsAt)
            const diff = reset - now
            if (diff <= 0) {
                setCountdown('Resetting...')
                return
            }
            const d = Math.floor(diff / 86400000)
            const h = Math.floor((diff % 86400000) / 3600000)
            const m = Math.floor((diff % 3600000) / 60000)
            const s = Math.floor((diff % 60000) / 1000)
            if (d > 0) {
                setCountdown(`${d}d ${h}h ${m}m ${s}s`)
            } else {
                setCountdown(`${h}h ${m}m ${s}s`)
            }
        }, 1000)
        return () => clearInterval(interval)
    }, [resetsAt])

    // Styles for bot icon to replace classes.chatBotIcon
    const botIconSx = { color: 'var(--color-primary)', mr: '5px' }

    useEffect(() => {
        window.scrollTo(0, 0)
        setVirtualTAOpenTime(true)
        if (activeCourseId) {
            setSelectedCourse(activeCourseId)
        } else {
            setInitialMessagesShown(true)
        }
    }, [activeCourseId])

    const getCourses = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BASE_URL}/api/v2/courses`,
                { headers: { 'Content-Type': 'application/json' } }
            )
            setCourses(res.data)
        } catch (error) {
            console.log('ERROR: ', error)
        }
    }

    const sortedCourses = useMemo(() => {
        if (!courses) return null
        return [...courses].sort((a, b) => {
            if (a.pinned && !b.pinned) return -1
            if (!a.pinned && b.pinned) return 1
            return 0
        })
    }, [courses])

    useEffect(() => {
        window.scrollTo(0, 0)
        if (
            userData.userFetchCompleted &&
            userData.user &&
            userData.user.id !== undefined
        ) {
            getCourses()
        }

        if (virtualTAOpenTime && !activeCourseId) {
            const delay = setTimeout(() => {
                setInitialMessagesShown(true)
            }, 2000)
            return () => clearTimeout(delay)
        }
    }, [
        userData.userFetchCompleted,
        userData.user,
        virtualTAOpenTime,
        activeCourseId,
    ])

    useEffect(() => {
        const fetchStaff = async () => {
            if (selectedCourse && selectedCourse !== 'Please choose a course') {
                try {
                    const res = await axios.get(
                        `${process.env.REACT_APP_BASE_URL}/api/v2/courses/${selectedCourse}/students`
                    )
                    const people = res.data.people || []
                    const staff = people.filter(
                        (p) => p.role === 'Instructor' || p.role === 'TA'
                    )
                    const instructor = people.find(
                        (p) => p.role === 'Instructor'
                    )

                    setCourseStaff({
                        emails: staff.map((s) => s.email),
                        professorLastName: instructor
                            ? instructor.lastName
                            : 'Staff',
                    })
                } catch (error) {
                    console.error('Error fetching staff:', error)
                }
            }
        }
        fetchStaff()
    }, [selectedCourse])

    useEffect(() => {
        if (initialMessagesShown) {
            const delay = setTimeout(() => {
                setShowCourseDropdown(true)
            }, 2000)
            return () => clearTimeout(delay)
        }
    }, [initialMessagesShown])

    const handleDropdownClick = () => {
        setDropdownPlaceholder('')
    }

    useEffect(() => {
        if (selectedCourse && courses) {
            const selectedCour = courses.find(
                (course) => course.id === selectedCourse
            )
            if (selectedCour) {
                setSelectedCourseName(selectedCour.name)
            }
        }
    }, [selectedCourse, courses])

    // Scroll only when a new message is added.
    const prevHistoryLengthRef = useRef(conversationHistory.length)
    useEffect(() => {
        if (conversationHistory.length > prevHistoryLengthRef.current) {
            if (conversationRef.current) {
                const lastChild = conversationRef.current.lastChild
                if (lastChild) {
                    lastChild.scrollIntoView({ behavior: 'smooth' })
                }
            }
        }
        prevHistoryLengthRef.current = conversationHistory.length
    }, [conversationHistory])

    const handleQuestionChange = (event) => {
        setQ(event.target.value)
    }

    const LoadingDots = () => {
        const [dots, setDots] = useState('Loading')
        useEffect(() => {
            const intervalId = setInterval(() => {
                setDots((prevDots) => {
                    return prevDots.length >= 18 ? 'Loading' : prevDots + '.'
                })
            }, 500)
            return () => clearInterval(intervalId)
        }, [])
        return (
            <Typography
                variant="body1"
                style={{
                    color: 'var(--color-text)',
                    fontWeight: 'bold',
                    fontStyle: 'italic',
                }}
            >
                {dots}
            </Typography>
        )
    }

    const fetchRecentChats = async (courseId) => {
        setIsFetchingHistory(true)
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BASE_URL}/v3/virtual-ta/history?courseId=${courseId}&limit=7`,
                { withCredentials: true }
            )
            if (res.data && res.data.chats && res.data.chats.length > 0) {
                const history = res.data.chats.map((chat) => ({
                    question: chat.question,
                    answer: chat.answer,
                    isCourseRelated: '',
                    loading: false,
                    feedbackGiven: false,
                    feedback: null,
                    _id: chat._id,
                    pinned: chat.pinned || false,
                }))
                setConversationHistory(history)
                // If we load history, we don't need to show welcome message
                setShowWelcomeMessage(false)
            } else {
                setConversationHistory([])
                setShowWelcomeMessage(true)
            }
            if (res.data) {
                const unlimited = Boolean(
                    res.data.isUnlimited ||
                        userIsAdmin ||
                        (res.data.tokenLimit === null &&
                            res.data.tokensRemaining === null)
                )
                setIsUnlimited(unlimited)
                setTokensRemaining(res.data.tokensRemaining)
                setTokenLimit(res.data.tokenLimit)
                setResetsAt(res.data.resetsAt)
                setLimitReached(
                    unlimited
                        ? false
                        : res.data.tokensRemaining !== null &&
                              res.data.tokensRemaining <= 0
                )
            }
        } catch (err) {
            console.error('Failed to fetch chat history', err)
            setConversationHistory([])
            setShowWelcomeMessage(true)
        } finally {
            setIsFetchingHistory(false)
        }
    }

    const handleCourseChange = (event) => {
        const courseId = event.target.value
        if (courseId === 'Please choose a course') {
            setSelectedCourse(null)
            setSelectedCourseName(null)
            setConversationHistory([])
            setShowWelcomeMessage(true)
        } else {
            const selectedCour = courses.find(
                (course) => course.id === courseId
            )
            if (selectedCour) {
                setSelectedCourseName(selectedCour.name)
            }
            setSelectedCourse(courseId)
            fetchRecentChats(courseId)
        }
        setInitialMessagesShown(false)
        setQ('')
    }

    function WelcomeMessage() {
        return (
            <div>
                <div
                    key="message1"
                    style={{
                        backgroundColor: 'var(--color-surface-2)',
                        color: 'var(--color-text)',
                        borderRadius: '10px',
                        padding: '10px',
                        margin: '10px',
                        marginLeft: '30px',
                        marginBottom: '15px',
                        alignSelf: 'flex-start',
                        maxWidth: '85%',
                    }}
                >
                    <p style={{ margin: '10px' }}>
                        Hi! I am your myTutor! You may ask any doubts related to
                        any concept as well as course details related queries.
                        Happy Learning!
                    </p>
                    <Person4Icon sx={botIconSx} />
                </div>
            </div>
        )
    }

    const handleFeedback = async (index, type) => {
        const ratingMapping = { like: 3, dislike: 1 }
        const ratingValue = ratingMapping[type]

        if (!fileToken) {
            console.error('No file token available for rating.')
            return
        }
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/v2/rate-file`,
                {
                    file_token: fileToken,
                    rating: ratingValue,
                }
            )
            console.log('Rate file response:', res.data)
        } catch (error) {
            console.error('Error rating file:', error)
        }

        setConversationHistory((prevHistory) =>
            prevHistory.map((msg, idx) =>
                idx === index
                    ? { ...msg, feedbackGiven: true, feedback: type }
                    : msg
            )
        )
    }

    const handleEmailStaff = () => {
        const studentName = userData?.user
            ? `${userData.user.firstName} ${userData.user.lastName}`
            : 'Student'
        const studentEmail = userData?.user?.email || ''
        const { emails, professorLastName } = courseStaff

        // Include student email in the list of recipients to CC them
        const allRecipients = studentEmail ? [...emails, studentEmail] : emails

        setConversationHistory((prevHistory) => [
            ...prevHistory,
            {
                type: 'contact-form',
                staffEmails: allRecipients,
                professorLastName: professorLastName,
                studentName: studentName,
                courseName: selectedCourseName,
                answer: 'Please fill out the form below to contact the course staff:',
                loading: false,
            },
        ])
    }

    const handleContactStaffSubmit = async (messageText, subject, toEmails) => {
        const studentName = userData?.user
            ? `${userData.user.firstName} ${userData.user.lastName}`
            : 'Student'
        const studentEmail = userData?.user?.email || ''

        await axios.post(`${process.env.REACT_APP_BASE_URL}/contact-us`, {
            name: studentName,
            email: studentEmail,
            message: messageText,
            subject: subject,
            toEmail: toEmails.join(','),
        })
    }

    const handleDownload = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BASE_URL}/v3/virtual-ta/download/${selectedCourse}`,
                { withCredentials: true, responseType: 'blob' }
            )
            const url = window.URL.createObjectURL(new Blob([res.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute(
                'download',
                `virtual-ta-${
                    selectedCourseName?.replace(/[^a-zA-Z0-9]/g, '_') || 'chat'
                }.txt`
            )
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Failed to download chats:', error)
        }
    }

    const handleDeleteChatHistory = async () => {
        try {
            await axios.delete(
                `${process.env.REACT_APP_BASE_URL}/v3/virtual-ta/history/${selectedCourse}`,
                { withCredentials: true }
            )
            setConversationHistory([])
            setDeleteDialogOpen(false)
        } catch (error) {
            console.error('Failed to delete chat history:', error)
            setDeleteDialogOpen(false)
        }
    }

    const handleTogglePin = async (chatId, index) => {
        try {
            const res = await axios.patch(
                `${process.env.REACT_APP_BASE_URL}/v3/virtual-ta/pin/${chatId}`,
                {},
                { withCredentials: true }
            )
            setConversationHistory((prev) =>
                prev.map((msg, i) =>
                    i === index ? { ...msg, pinned: res.data.chat.pinned } : msg
                )
            )
        } catch (error) {
            console.error('Failed to toggle pin:', error)
        }
    }

    const handleCancelContact = (index) => {
        setConversationHistory((prev) => prev.filter((_, i) => i !== index))
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        setError('')

        // Guard against accidental paste of huge text (e.g. terminal output)
        const MAX_QUERY_CHARS = 2000
        if (!question.trim()) {
            setError('Please enter a question.')
            return
        }
        if (question.length > MAX_QUERY_CHARS) {
            setError(
                `Your question is too long (${question.length} characters). Please keep it under ${MAX_QUERY_CHARS} characters.`
            )
            return
        }

        setQ('')
        setQueryInProgress(true)

        try {
            const newMessage = {
                question,
                answer: (
                    <>
                        <CircularProgress size={20} />
                        <LoadingDots />
                    </>
                ),
                isCourseRelated: '',
                loading: true,
                feedbackGiven: false,
                feedback: null,
            }

            setConversationHistory((prevHistory) => [
                ...prevHistory,
                newMessage,
            ])

            let answer = '',
                file_token = '',
                chatId = null
            try {
                const userTimezone =
                    Intl.DateTimeFormat().resolvedOptions().timeZone
                const response = await fetch(
                    `${process.env.REACT_APP_BASE_URL}/v3/virtual-ta/ask-question`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            courseId: selectedCourse,
                            question: question,
                            userTimezone: userTimezone,
                        }),
                        credentials: 'include',
                    }
                )

                let data
                if (!response.ok) {
                    try {
                        data = await response.json()
                        if (response.status === 429) {
                            setLimitReached(true)
                            setTokensRemaining(0)
                            if (data.tokenLimit) {
                                setTokenLimit(data.tokenLimit)
                            }
                            if (data.resetsAt) {
                                setResetsAt(data.resetsAt)
                            }
                            throw new Error(
                                data.message ||
                                    "Weekly token limit reached. You've used all 5,000 tokens for this week."
                            )
                        }
                    } catch (e) {
                        if (e.message && e.message.includes('token limit'))
                            throw e
                    }
                    if (data && (data.error || data.message)) {
                        throw new Error(data.error || data.message)
                    }
                    throw new Error(`HTTP error! Status: ${response.status}`)
                }

                data = await response.json()
                answer = data.response
                file_token = data.file_token
                setFileToken(file_token)

                // Tokens and chat are now saved atomically in askQuestionSecure
                if (data.chat && data.chat._id) {
                    chatId = data.chat._id
                }
                if (
                    data.isUnlimited !== undefined ||
                    data.tokensRemaining !== undefined
                ) {
                    const unlimited = Boolean(
                        data.isUnlimited ||
                            userIsAdmin ||
                            (data.tokenLimit === null &&
                                data.tokensRemaining === null)
                    )
                    setIsUnlimited(unlimited)
                    setTokensRemaining(data.tokensRemaining)
                    setTokenLimit(data.tokenLimit)
                    if (data.resetsAt) setResetsAt(data.resetsAt)
                    setLimitReached(
                        unlimited
                            ? false
                            : data.tokensRemaining !== null &&
                                  data.tokensRemaining <= 0
                    )
                }
            } catch (error) {
                console.error('Error:', error.message)
                answer =
                    error.message &&
                    error.message !== 'Failed to fetch' &&
                    !error.message.includes('HTTP error')
                        ? error.message
                        : 'Error: Could not connect to the AI server. Please ensure the Django backend is running.'
            }

            setConversationHistory((prevHistory) =>
                prevHistory.map((message) =>
                    message === newMessage
                        ? {
                              ...message,
                              answer: answer,
                              loading: false,
                              _id: chatId || message._id,
                              pinned: message.pinned || false,
                          }
                        : message
                )
            )

            setQ('')
            setQueryInProgress(false)
        } catch (err) {
            console.error(err)
            const errorMsg =
                err.message && err.message !== 'Network response was not ok'
                    ? err.message
                    : 'There was an error processing your request. Please try again.'
            setError(errorMsg)
            setConversationHistory((prevHistory) =>
                prevHistory.map((message) =>
                    message.loading
                        ? { ...message, answer: errorMsg, loading: false }
                        : message
                )
            )
            setQueryInProgress(false)
        }
    }

    if (userData.userFetchCompleted && !userData.user?.id) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: 'var(--color-bg)',
                }}
            >
                <Typography variant="h6" color="var(--color-text-muted)">
                    You need to login to access the Virtual TA
                </Typography>
            </Box>
        )
    }

    return (
        <Box
            sx={{
                backgroundColor: 'var(--color-bg)',
                color: 'var(--color-text)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                flex: 1,
                overflow: 'hidden',
                zIndex: 1105,
            }}
        >
            {/* Sticky Header */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backdropFilter: 'blur(10px)',
                    backgroundColor:
                        'rgba(var(--color-bg-rgb, 255, 255, 255), 0.8)',
                    borderBottom: '1px solid var(--color-border)',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 'bold', m: 0 }}>
                        {userData?.user?.role === 'Instructor' ? (
                            <span style={{ color: 'var(--color-primary)' }}>
                                <i>my</i>TA
                            </span>
                        ) : (
                            <span style={{ color: 'var(--color-primary)' }}>
                                <i>my</i>Tutor
                            </span>
                        )}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {selectedCourse &&
                            selectedCourse !== 'Please choose a course' && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() =>
                                            history.push({
                                                pathname: '/virtual-ta',
                                                state: {
                                                    courseId: selectedCourse,
                                                },
                                            })
                                        }
                                        sx={{
                                            borderRadius: '20px',
                                            textTransform: 'none',
                                            fontSize: '0.75rem',
                                            borderColor: 'var(--color-border)',
                                            color: 'var(--color-text)',
                                            '&:hover': {
                                                backgroundColor:
                                                    'var(--color-surface-2)',
                                                borderColor:
                                                    'var(--color-primary-hover)',
                                            },
                                        }}
                                    >
                                        Chat History ↗
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<EmailIcon />}
                                        onClick={handleEmailStaff}
                                        sx={{
                                            borderRadius: '20px',
                                            textTransform: 'none',
                                            fontSize: '0.75rem',
                                            backgroundColor:
                                                'var(--color-primary)',
                                            color: '#ffffff !important',
                                        }}
                                    >
                                        Email Staff
                                    </Button>
                                </Box>
                            )}
                        {handleClose && (
                            <IconButton
                                onClick={handleClose}
                                size="small"
                                sx={{
                                    ml: 0.5,
                                    color: 'var(--color-text-muted)',
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {showCourseDropdown && sortedCourses && (
                    <CourseDropdown
                        courses={sortedCourses}
                        selectedCourse={selectedCourse}
                        handleDropdownClick={handleDropdownClick}
                        handleCourseChange={handleCourseChange}
                        dropdownPlaceholder={dropdownPlaceholder}
                    />
                )}
            </Box>

            {/* Middle Chat Section */}
            <Box
                ref={conversationRef}
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {initialMessagesShown && (
                    <Box>
                        <Box
                            key="message1"
                            sx={{
                                backgroundColor: 'var(--color-surface-2)',
                                color: 'var(--color-text)',
                                borderRadius: '20px',
                                borderBottomLeftRadius: '0',
                                padding: '12px 16px',
                                margin: '10px 10px 15px 20px',
                                alignSelf: 'flex-start',
                                maxWidth: '75%',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            }}
                        >
                            <Typography sx={{ margin: 0, fontSize: '0.95rem' }}>
                                Hi! I am your virtual teaching assistant. You
                                may ask all your educational doubts here!
                            </Typography>
                        </Box>
                        <Person4Icon sx={botIconSx} />
                        <Box
                            key="message2"
                            sx={{
                                backgroundColor: 'var(--color-surface-2)',
                                color: 'var(--color-text)',
                                borderRadius: '20px',
                                borderBottomLeftRadius: '0',
                                padding: '12px 16px',
                                margin: '10px 10px 15px 20px',
                                alignSelf: 'flex-start',
                                maxWidth: '75%',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            }}
                        >
                            <Typography sx={{ margin: 0, fontSize: '0.95rem' }}>
                                Please choose a course to proceed further...
                            </Typography>
                        </Box>
                        <Person4Icon sx={botIconSx} />
                    </Box>
                )}

                {showWelcomeMessage && <WelcomeMessage />}

                {isFetchingHistory && (
                    <Box
                        sx={{ display: 'flex', justifyContent: 'center', p: 4 }}
                    >
                        <CircularProgress
                            size={24}
                            sx={{ color: 'var(--color-primary)' }}
                        />
                    </Box>
                )}

                {!isFetchingHistory &&
                    conversationHistory.map((message, indx) => (
                        <Box
                            key={indx}
                            sx={{
                                marginBottom: '15px',
                                userSelect: 'text',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {message.type === 'contact-form' ? (
                                <Box
                                    sx={{
                                        p: 2,
                                        m: 1,
                                        backgroundColor:
                                            'var(--color-surface-1)',
                                        borderRadius: '15px',
                                        border: '1px solid var(--color-border)',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                    }}
                                >
                                    <ContactForm
                                        staffEmails={message.staffEmails}
                                        professorLastName={
                                            message.professorLastName
                                        }
                                        studentName={message.studentName}
                                        courseName={message.courseName}
                                        onSubmit={(msg, subj) =>
                                            handleContactStaffSubmit(
                                                msg,
                                                subj,
                                                message.staffEmails
                                            )
                                        }
                                        onCancel={() =>
                                            handleCancelContact(indx)
                                        }
                                    />
                                </Box>
                            ) : (
                                <>
                                    {/* Question Bubble */}
                                    {message.question && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row-reverse',
                                                alignItems: 'flex-end',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    backgroundColor:
                                                        'var(--color-primary)',
                                                    color: '#ffffff !important',
                                                    borderRadius: '20px',
                                                    borderBottomRightRadius: 0,
                                                    padding: '12px 16px',
                                                    margin: '0 10px 10px 90px',
                                                    maxWidth: '80%',
                                                    textAlign: 'left',
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'break-word',
                                                    whiteSpace: 'pre-wrap',
                                                    boxShadow:
                                                        '0 2px 8px rgba(0,0,0,0.1)',
                                                    fontSize: '0.95rem',
                                                }}
                                            >
                                                {message.question}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Answer Bubble and Feedback */}
                                    <Box
                                        sx={{
                                            padding: '1px 0 5px 20px',
                                            maxWidth: '85%',
                                            userSelect: 'text',
                                            zIndex: 1108,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                backgroundColor:
                                                    'var(--color-surface-2)',
                                                color: 'var(--color-text)',
                                                width: '100%',
                                                padding: '16px',
                                                borderRadius: '20px',
                                                borderBottomLeftRadius: 0,
                                                margin: '0 10px 10px 0',
                                                boxShadow:
                                                    '0 2px 8px rgba(0,0,0,0.05)',
                                                fontSize: '0.95rem',
                                                '& p': { mt: '12px', mb: 0 },
                                                '& h1, & h2, & h3, & h4, & h5, & h6':
                                                    { mt: 0, mb: '12px' },
                                                '&:first-of-type p:first-of-type':
                                                    { mt: 0 },
                                            }}
                                        >
                                            {typeof message.answer ===
                                            'string' ? (
                                                <ReactMarkdown
                                                    components={{
                                                        a: ({
                                                            node,
                                                            href,
                                                            children,
                                                            ...props
                                                        }) => {
                                                            const isInternal =
                                                                href &&
                                                                (href.startsWith(
                                                                    '/'
                                                                ) ||
                                                                    href.startsWith(
                                                                        '?'
                                                                    ))
                                                            return (
                                                                <a
                                                                    href={href}
                                                                    onClick={(
                                                                        e
                                                                    ) => {
                                                                        if (
                                                                            isInternal
                                                                        ) {
                                                                            e.preventDefault()
                                                                            history.push(
                                                                                href
                                                                            )
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        color: '#2563eb',
                                                                        textDecoration:
                                                                            'underline',
                                                                        fontWeight: 600,
                                                                        cursor: 'pointer',
                                                                    }}
                                                                    target={
                                                                        isInternal
                                                                            ? undefined
                                                                            : '_blank'
                                                                    }
                                                                    rel={
                                                                        isInternal
                                                                            ? undefined
                                                                            : 'noopener noreferrer'
                                                                    }
                                                                    {...props}
                                                                >
                                                                    {children}
                                                                </a>
                                                            )
                                                        },
                                                    }}
                                                >
                                                    {message.answer}
                                                </ReactMarkdown>
                                            ) : (
                                                message.answer
                                            )}
                                        </Box>
                                        <Person4Icon sx={botIconSx} />

                                        {/* Feedback Buttons */}
                                        {!message.loading &&
                                            !message.feedbackGiven &&
                                            indx ===
                                                conversationHistory.length -
                                                    1 && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'flex-start',
                                                        margin: '5px 0 10px 20px',
                                                    }}
                                                >
                                                    <Button
                                                        onClick={() =>
                                                            handleFeedback(
                                                                indx,
                                                                'like'
                                                            )
                                                        }
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{
                                                            marginRight: '10px',
                                                            borderRadius:
                                                                '15px',
                                                            color: 'var(--color-text)',
                                                            borderColor:
                                                                'var(--color-border)',
                                                        }}
                                                    >
                                                        <ThumbUpIcon
                                                            sx={{
                                                                marginRight:
                                                                    '5px',
                                                                fontSize:
                                                                    '1.2rem',
                                                            }}
                                                        />{' '}
                                                        Like
                                                    </Button>
                                                    <Button
                                                        onClick={() =>
                                                            handleFeedback(
                                                                indx,
                                                                'dislike'
                                                            )
                                                        }
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{
                                                            borderRadius:
                                                                '15px',
                                                            color: 'var(--color-text)',
                                                            borderColor:
                                                                'var(--color-border)',
                                                        }}
                                                    >
                                                        <ThumbDownIcon
                                                            sx={{
                                                                marginRight:
                                                                    '5px',
                                                                fontSize:
                                                                    '1.2rem',
                                                            }}
                                                        />{' '}
                                                        Dislike
                                                    </Button>
                                                </Box>
                                            )}

                                        {/* Feedback Display */}
                                        {!message.loading &&
                                            message.feedbackGiven && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'flex-start',
                                                        margin: '0 0 10px 20px',
                                                    }}
                                                >
                                                    {message.feedback ===
                                                    'like' ? (
                                                        <>
                                                            <ThumbUpIcon
                                                                sx={{
                                                                    marginRight:
                                                                        '5px',
                                                                    color: 'green',
                                                                    fontSize:
                                                                        '1.2rem',
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontStyle:
                                                                        'italic',
                                                                    color: 'var(--color-text-muted)',
                                                                }}
                                                            >
                                                                Liked
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ThumbDownIcon
                                                                sx={{
                                                                    marginRight:
                                                                        '5px',
                                                                    color: 'red',
                                                                    fontSize:
                                                                        '1.2rem',
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontStyle:
                                                                        'italic',
                                                                    color: 'var(--color-text-muted)',
                                                                }}
                                                            >
                                                                Disliked
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Box>
                                            )}
                                    </Box>
                                </>
                            )}
                        </Box>
                    ))}
            </Box>

            {/* Fixed Footer Input Box */}
            {showCourseDropdown && !initialMessagesShown && (
                <Box
                    sx={{
                        p: 2,
                        borderTop: '1px solid var(--color-border)',
                        backgroundColor: 'var(--color-surface-1)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    {unlimitedAccess ? (
                        <Typography
                            sx={{
                                textAlign: 'center',
                                fontSize: '0.75rem',
                                color: 'var(--color-primary)',
                                fontWeight: 600,
                            }}
                        >
                            Tokens: Unlimited (Admin)
                        </Typography>
                    ) : (
                        !limitReached &&
                        tokensRemaining !== null &&
                        tokenLimit !== null && (
                            <Typography
                                sx={{
                                    textAlign: 'center',
                                    fontSize: '0.75rem',
                                    color: 'var(--color-text-muted)',
                                }}
                            >
                                Tokens remaining:{' '}
                                {tokensRemaining.toLocaleString()} /{' '}
                                {tokenLimit.toLocaleString()} (this week)
                            </Typography>
                        )
                    )}

                    <form
                        onSubmit={handleSubmit}
                        autoComplete="off"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                position: 'relative',
                            }}
                        >
                            {limitReached &&
                            !unlimitedAccess &&
                            tokenLimit !== null ? (
                                <Box
                                    sx={{
                                        p: 1.5,
                                        textAlign: 'center',
                                        width: '100%',
                                        backgroundColor:
                                            'var(--color-surface-2)',
                                        borderRadius: '25px',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: 'var(--color-text)',
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        You've used all{' '}
                                        {tokenLimit.toLocaleString()} tokens for
                                        this week.
                                    </Typography>
                                    {resetsAt && (
                                        <Typography
                                            sx={{
                                                color: 'var(--color-primary)',
                                                fontSize: '0.8rem',
                                                mt: 0.5,
                                                fontFamily: 'monospace',
                                            }}
                                        >
                                            Resets in {countdown}
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <>
                                    <input
                                        type="text"
                                        id="question"
                                        name="question"
                                        autoComplete="off"
                                        autoCorrect="off"
                                        spellCheck="false"
                                        placeholder="Write your question..."
                                        value={question}
                                        onChange={handleQuestionChange}
                                        style={{
                                            height: '50px',
                                            width: '100%',
                                            padding: '12px 50px 12px 20px',
                                            margin: '0',
                                            display: 'block',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: '25px',
                                            boxSizing: 'border-box',
                                            outline: 'none',
                                            backgroundColor:
                                                'var(--color-surface-2)',
                                            color: 'var(--color-text)',
                                            fontSize: '0.95rem',
                                        }}
                                    />
                                    {!queryInProgress && (
                                        <IconButton
                                            type="submit"
                                            disabled={!question?.trim()}
                                            sx={{
                                                position: 'absolute',
                                                right: '5px',
                                                color: question?.trim()
                                                    ? '#ffffff'
                                                    : 'var(--color-text-muted)',
                                                backgroundColor:
                                                    question?.trim()
                                                        ? 'var(--color-primary)'
                                                        : 'transparent',
                                                '&:hover': {
                                                    backgroundColor:
                                                        question?.trim()
                                                            ? 'var(--color-primary-hover)'
                                                            : 'transparent',
                                                },
                                                '&.Mui-disabled': {
                                                    color: 'var(--color-text-muted)',
                                                    backgroundColor:
                                                        'transparent',
                                                },
                                                transition: 'all 0.2s',
                                            }}
                                        >
                                            <SendIcon
                                                fontSize="small"
                                                sx={{
                                                    color: question?.trim()
                                                        ? '#ffffff !important'
                                                        : 'inherit',
                                                }}
                                            />
                                        </IconButton>
                                    )}
                                </>
                            )}
                        </Box>
                    </form>
                </Box>
            )}

            {error && (
                <Box
                    sx={{
                        p: 2,
                        backgroundColor: 'var(--color-error-bg)',
                        color: 'var(--color-error-text)',
                        textAlign: 'center',
                        fontSize: '0.9rem',
                    }}
                >
                    {error}
                </Box>
            )}

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete Chat History</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete all Virtual TA chat
                        history for this course? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteChatHistory}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default VirtualTA
